import React, { useState } from "react";
import styled from "styled-components";
import { Order } from "../../api/orderService";
import OrderDetailsModal from "./OrderDetailsModal";

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border-radius: 0.7rem;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(233, 125, 125, 0.04);
`;
const Th = styled.th`
  background: #faf9f9;
  color: #e97d7d;
  font-weight: 700;
  padding: 1rem;
  border-bottom: 1.5px solid #f0eaea;
`;
const Td = styled.td`
  padding: 1rem;
  border-bottom: 1px solid #f0eaea;
`;
const Tr = styled.tr<{ odd?: boolean }>`
  background: ${(p) => (p.odd ? "#fff2f2" : "#fff")};
`;
const StatusBadge = styled.span<{ status: string }>`
  display: inline-block;
  padding: 0.4rem 0.8rem;
  border-radius: 1rem;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  background: ${(props) => {
    switch (props.status) {
      case "pending":
        return "#ffc107";
      case "processing":
        return "#17a2b8";
      case "shipped":
        return "#007bff";
      case "delivered":
        return "#28a745";
      case "cancelled":
        return "#dc3545";
      default:
        return "#6c757d";
    }
  }};
  color: #fff;
`;
const ActionBtn = styled.button`
  background: #e97d7d;
  color: #fff;
  border: none;
  border-radius: 1.2rem;
  padding: 0.5rem 1.2rem;
  margin-right: 0.5rem;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: #d96a6a;
  }
`;

const OrdersTable: React.FC<{
  orders: Order[];
  onSelectOrder: (order: Order) => void;
  onStatusUpdate: (
    orderId: string,
    status: string,
    trackingUrl?: string,
    trackingDetails?: string
  ) => Promise<void>;
}> = ({ orders, onSelectOrder, onStatusUpdate }) => {
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  const handleViewOrder = (order: Order) => {
    setSelectedOrder(order);
  };

  const handleCloseModal = () => {
    setSelectedOrder(null);
  };

  const handleStatusUpdate = async (
    orderId: string,
    status: string,
    trackingUrl?: string,
    trackingDetails?: string
  ) => {
    await onStatusUpdate(orderId, status, trackingUrl, trackingDetails);
    setSelectedOrder(null);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return "Invalid Date";
    }
  };

  return (
    <>
      <Table>
        <thead>
          <tr>
            <Th>Order ID</Th>
            <Th>Customer</Th>
            <Th>Total</Th>
            <Th>Status</Th>
            <Th>Date</Th>
            <Th>Actions</Th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order, idx) => (
            <Tr key={order.id} odd={idx % 2 === 1}>
              <Td>{order.id}</Td>
              <Td>
                {order.shippingAddress?.name || order.customerName || "N/A"}
              </Td>
              <Td>${(order.total || order.totalAmount || 0).toFixed(2)}</Td>
              <Td>
                <StatusBadge status={order.status}>{order.status}</StatusBadge>
              </Td>
              <Td>{formatDate(order.createdAt)}</Td>
              <Td>
                <ActionBtn onClick={() => handleViewOrder(order)}>
                  View
                </ActionBtn>
              </Td>
            </Tr>
          ))}
        </tbody>
      </Table>

      {selectedOrder && (
        <OrderDetailsModal
          order={selectedOrder}
          onClose={handleCloseModal}
          onStatusUpdate={handleStatusUpdate}
        />
      )}
    </>
  );
};
export default OrdersTable;
